import classnames from 'classnames'
import { CSSProperties, useRef } from 'react'

import { useQuinceNprogressRouter as useRouter } from '@src/modules/navigation'
import LAYOUT_TYPES from '@src/quince-site-common/src/constants/layoutTypes'
import { TrackingMetaData } from '@src/quince-site-common/src/domains/CustomSection'
import Link from '@src/quince-site-common/src/domains/Link'
import { getIsNativeViewClient } from '@src/quince-site-common/src/helpers/getIsNativeViewClient'
import useDeviceInfo from '@src/quince-site-common/src/hooks/useDeviceInfo'
import checkIsInFirstFold from '@src/quince-site-common/src/utils/checkIsInFirstFold'
import getMaxWidthInPx from '@src/quince-site-common/src/utils/getMaxWidthInPx'
import { FINDING_METHOD_AGGREGATE, setFindingMethod } from '@src/telemetry/findingMethod'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackClickEvent } from '@src/telemetry/track'

import { RichText, RichTextProps } from '../../molecules'
import { default as Button, ButtonProps } from '../button'
import { Image, ImageProps } from '../image'
import Video, { VIDEO_LOADING_TYPE, VIDEO_PRELOAD_TYPE, VideoProps } from '../video'

import styles from './contentWithBackgroundImage.module.scss'
import { BANNER_TYPE } from './contsants'

export interface ContentWithBackgroundImageProps {
  imageProps?: ImageProps
  videoProps?: VideoProps
  titleRichTextProps?: RichTextProps
  descriptionRichTextProps?: RichTextProps
  richTextsItems?: RichTextProps[]
  buttonPropsList?: ButtonProps[]
  hasButton?: boolean
  className?: string
  contentClassName?: string
  contentInnerWrapper?: string
  buttonsWrapperClassName?: string
  style?: CSSProperties
  layoutType?: LAYOUT_TYPES
  tracker?: string
  link?: Link
  styleObject?: CSSProperties
  overrideMaxImageWidth1600?: boolean
  trackingMetaData?: TrackingMetaData
  shouldShowOptimisedTheVideo?: boolean
  fireImageClickEventAlways?: boolean
  bannerType?: string
}

export const ContentWithBackgroundImage = (props: ContentWithBackgroundImageProps) => {
  const { layoutType = LAYOUT_TYPES.DEFAULT, trackingMetaData, className = '', hasButton = false, bannerType } = props
  const router = useRouter()
  const isWebView = getIsNativeViewClient()

  const wrapperClasses = classnames(
    styles.contentWithBgImageContainer,
    styles.bannerWrap,
    props.link?.navigationPath && styles.bannerWrap__clickable,
    styles.bannerImage,
    className,
    'position--relative',
    styles[`bannerWrap__${layoutType}`],
  )

  const containerRef = useRef<HTMLDivElement>(null)
  const deviceInfo = useDeviceInfo()

  const handleImageClick = () => {
    if (props.link?.navigationPath) {
      if (!props.buttonPropsList?.length || props.fireImageClickEventAlways) {
        // Note [srimaraj :4apr2025] : Lint bypass to unblock build since i have limited context on this and its impact if isInFirstFold set to false
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        const isInFirstFold = containerRef.current ? checkIsInFirstFold(containerRef.current) : false
        // only send this event if Image doesn't have any UI button
        setFindingMethod[FINDING_METHOD_AGGREGATE.CAMPAIGN]?.({
          elementName: trackingMetaData?.context?.contentContainerName ?? '',
          elementId: trackingMetaData?.context?.trackingId ?? '',
        })
        trackClickEvent(TELEMETRY_EVENT_NAMES.LINK_CLICK, {
          trackInternal: true,
          trackGA: true,
          // @i18n-ignore
          action: 'link_click',
          // @i18n-ignore
          category: 'navigation',
          value: props.link.navigationPath,
          label: `${props.tracker}__contentWithBackgroundImage`,
          context: {
            ...trackingMetaData?.context,
            isInFirstFold: isInFirstFold,
          },
        })
      }
      const { navigationPath } = props.link
      if (isWebView) {
        try {
          window.ReactNativeWebView?.postMessage(
            JSON.stringify({
              // @i18n-ignore
              action: 'NAVIGATION',
              url: window.location.href,
              newURL: props.link.navigationPath.split('?')[0],
              nextURL: props.link.navigationPath,
            }),
          )
        } catch (error) {
          // open in webview if postmessage fails
          router.push(navigationPath)
          console.error('Error in sending message to native webview ', error)
        }
      } else {
        router.push(navigationPath)
      }
    }
  }

  return (
    <div
      ref={containerRef}
      className={wrapperClasses}
      style={{ ...props.style, ...props.styleObject }}
      onClick={() => {
        handleImageClick()
      }}
    >
      {props.videoProps?.url && (
        <Video
          {...props.videoProps}
          muted
          autoplay
          loop
          controls={false}
          loadingType={VIDEO_LOADING_TYPE.Lazy}
          preload={VIDEO_PRELOAD_TYPE.Auto}
          posterImage={props.imageProps}
          className={classnames(styles.bannerMedia, props.videoProps.className)}
        />
      )}
      {props.imageProps?.url && !props.videoProps?.url && (
        <Image
          alt={props.imageProps.alt}
          {...props.imageProps}
          className={classnames(styles.bannerMedia, props.imageProps.className)}
          overrideMaxImageWidth1600={props.overrideMaxImageWidth1600}
          maxWidth={getMaxWidthInPx({
            maxWidthDesktopInRem: styles.backgroundImageWidthDesktop,
            maxWidthMobileInRem: styles.backgroundImageWidthMobile,
            deviceType: deviceInfo.deviceType,
          })}
        />
      )}
      <div
        className={classnames(
          styles.content,
          props.contentClassName,
          bannerType === BANNER_TYPE.FINAL_SALE && styles.content__noBottomPadding,
        )}
      >
        <div className={classnames(styles.contentInnerWrapper, props.contentInnerWrapper)}>
          {props.richTextsItems?.map((richTextItem, idx) => (
            <RichText key={idx} {...richTextItem} />
          ))}
          {hasButton && props.buttonPropsList?.length && (
            <div className={classnames(styles.buttonWrapper, props.buttonsWrapperClassName)}>
              {props.buttonPropsList.map((buttonProps: ButtonProps) => {
                return (
                  <Button
                    {...buttonProps}
                    key={buttonProps.id}
                    className={classnames(styles.actionButton, buttonProps.className)}
                    trackConfig={
                      props.tracker
                        ? {
                            trackInternal: true,
                            trackGA: true,
                            // @i18n-ignore
                            action: 'button_click',
                            // @i18n-ignore
                            category: 'navigation',
                            value: `${buttonProps.href}`,
                            label: `${props.tracker}__contentWithBackgroundImage__button`,
                            context: trackingMetaData?.context,
                          }
                        : undefined
                    }
                  />
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ContentWithBackgroundImage
